import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Clock, Users, FileCheck2, Star, ChevronDown } from "lucide-react";
import { api, waLink } from "../lib/api";
import { getServiceMeta } from "../lib/serviceIcons";

const STATS = [
  { v: "25,000+", l: "Businesses served" },
  { v: "4.9 / 5", l: "Avg. rating" },
  { v: "72 hrs", l: "Avg. turnaround" },
  { v: "350+", l: "CA & lawyers" },
];

const TESTIMONIALS = [
  {
    name: "Rakesh Mehta",
    role: "Founder, Mehta Textiles",
    img: "https://images.unsplash.com/photo-1672685667592-0392f458f46f?crop=entropy&cs=srgb&fm=jpg&w=200",
    quote: "FinSahayak handled our GST and trademark in 4 days. The WhatsApp updates feel like having a CA on speed-dial.",
  },
  {
    name: "Anita Reddy",
    role: "Freelance Designer",
    img: "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&w=200",
    quote: "ITR done in under 30 minutes from the form to the acknowledgement. I'm switching for life.",
  },
  {
    name: "Vikram Singh",
    role: "MSME owner, Jaipur",
    img: "https://images.pexels.com/photos/26872232/pexels-photo-26872232.jpeg?auto=compress&cs=tinysrgb&w=200",
    quote: "Document upload is straight from phone camera. Even my old man with no tech background could do it.",
  },
];

const FAQS = [
  { q: "How long does GST Registration take?", a: "Most GST registrations are completed in 5–7 working days once all documents are submitted." },
  { q: "Are my documents secure?", a: "All documents are stored on encrypted cloud storage and accessible only to assigned professionals." },
  { q: "Can I file ITR for previous years?", a: "Yes, we file belated ITRs (last 2 financial years) including penalty computation." },
  { q: "Do you provide refunds?", a: "Yes — full refund within 24 hours if no work has started. See refund policy for details." },
  { q: "How will I receive updates?", a: "Real-time WhatsApp and email updates at every step. You also get a customer portal login post conversion." },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services.slice(0, 6)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="hero-grain" />
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/c378d17e-2aac-4835-9637-f398abd3e1cf/images/d5e171574214bc85b6cdabd948226d66fa1ec8dcccd4f84725191ed5d09228e7.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative container-pad mx-auto max-w-[1400px] pt-16 md:pt-28 pb-24 md:pb-40 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <span className="label-eyebrow" data-testid="hero-eyebrow">For India's MSMEs & freelancers</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-medium tracking-tighter text-[#121C17] leading-[1.05]">
              Compliance, <span className="italic text-[#1E4D3B]">simplified.</span><br />
              From ₹99, done on WhatsApp.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[#5C6661] leading-relaxed">
              GST, ITR, trademark, company incorporation and more — handled by qualified professionals with real human chat. Upload your documents, get a quote, and we'll take it from there.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
              <Link to="/services" data-testid="hero-cta-get-started"
                className="btn-primary rounded-full px-6 py-3.5 text-sm font-medium inline-flex items-center gap-2">
                Get Started <ArrowUpRight size={16} />
              </Link>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp"
                className="rounded-full px-6 py-3.5 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-2 hover:border-[#1E4D3B]">
                <span className="w-2 h-2 rounded-full bg-[#25D366]" /> Talk on WhatsApp
              </a>
            </div>
            <div className="mt-12 flex gap-3 flex-wrap text-xs text-[#5C6661]" data-testid="hero-trust-badges">
              <span className="px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] inline-flex items-center gap-1.5"><ShieldCheck size={12} /> ISO 27001 secure</span>
              <span className="px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] inline-flex items-center gap-1.5"><Clock size={12} /> 72 hr turnaround</span>
              <span className="px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] inline-flex items-center gap-1.5"><Star size={12} /> 4.9★ on Google</span>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="hero-stats">
            {STATS.map((s, i) => (
              <div key={i} className="bento-card p-6">
                <div className="text-3xl font-display font-medium text-[#1E4D3B]">{s.v}</div>
                <div className="text-xs mt-1 text-[#5C6661]">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="section-pad bg-[#F3F1EC]" data-testid="services-preview">
        <div className="container-pad mx-auto max-w-[1400px]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <span className="label-eyebrow">Services</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-display font-medium tracking-tight text-[#121C17]">Everything your business needs to stay compliant.</h2>
            </div>
            <Link to="/services" data-testid="services-preview-view-all" className="text-sm text-[#1E4D3B] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">View all services <ArrowUpRight size={14} /></Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s) => {
              const { icon: Icon, accent, bgSolid } = getServiceMeta(s);
              return (
                <Link key={s.id} to={`/apply/${s.id}`} data-testid={`service-card-${s.id}`} className="bento-card p-6 group flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: bgSolid, color: accent }}>
                      <Icon size={22} />
                    </div>
                    <span className="label-eyebrow text-[10px]">{s.category}</span>
                  </div>
                  <h3 className="mt-5 text-xl font-display font-medium text-[#121C17]">{s.name}</h3>
                  <p className="mt-2 text-sm text-[#5C6661] flex-1">{s.tagline}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-lg font-display font-medium" style={{ color: accent }}>{s.price_label}</span>
                    <span className="rounded-full px-3 py-1.5 text-xs font-medium text-white inline-flex items-center gap-1 group-hover:gap-1.5 transition-all" style={{ background: accent }}>
                      Apply <ArrowUpRight size={12} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad" data-testid="how-it-works-section">
        <div className="container-pad mx-auto max-w-[1400px] grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">How it works</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-medium tracking-tight text-[#121C17]">A 4-step flow that respects your time.</h2>
            <p className="mt-4 text-[#5C6661] leading-relaxed">From the moment you pick a service to receiving your certificate — every step is tracked, transparent and chat-driven.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { n: "01", t: "Pick a service", d: "Browse 9+ compliance services with transparent pricing.", icon: FileCheck2 },
              { n: "02", t: "Upload docs", d: "Drag-and-drop or shoot from your phone camera.", icon: ShieldCheck },
              { n: "03", t: "We process", d: "Qualified CAs and lawyers take it from there.", icon: Users },
              { n: "04", t: "Stay updated", d: "WhatsApp + email at every milestone.", icon: Clock },
            ].map((step) => (
              <div key={step.n} className="bento-card p-6">
                <step.icon className="text-[#1E4D3B]" size={22} />
                <div className="mt-6 flex items-baseline gap-3">
                  <span className="text-xs label-eyebrow">{step.n}</span>
                  <h3 className="text-lg font-display font-medium text-[#121C17]">{step.t}</h3>
                </div>
                <p className="mt-2 text-sm text-[#5C6661]">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[#121C17] text-[#FDFCFB]" data-testid="testimonials-section">
        <div className="container-pad mx-auto max-w-[1400px]">
          <span className="label-eyebrow" style={{ color: "#D17B5D" }}>Loved by 25,000+ businesses</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-display font-medium tracking-tight">Hear it from the people we serve.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid={`testimonial-${i}`}>
                <blockquote className="text-base leading-relaxed text-white/90">"{t.quote}"</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-white/60">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" data-testid="faq-section">
        <div className="container-pad mx-auto max-w-[900px]">
          <span className="label-eyebrow">FAQ</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-display font-medium tracking-tight text-[#121C17]">Questions, answered.</h2>
          <div className="mt-10 space-y-2">
            {FAQS.map((f, i) => (
              <div key={i} className="bento-card overflow-hidden" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between p-6 text-left" data-testid={`faq-toggle-${i}`}>
                  <span className="font-medium text-[#121C17]">{f.q}</span>
                  <ChevronDown size={18} className={`transition-transform text-[#5C6661] ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-[#5C6661] leading-relaxed text-sm">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-pad" data-testid="final-cta-section">
        <div className="container-pad mx-auto max-w-[1400px]">
          <div className="rounded-3xl bg-[#1E4D3B] text-[#FDFCFB] p-10 md:p-16 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-display font-medium tracking-tight">Ready to stop worrying about compliance?</h2>
              <p className="mt-4 text-white/80 max-w-md">Start with any service in under 2 minutes. No paperwork pile-ups, no chartered accountants who never reply.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/services" data-testid="final-cta-services"
                className="rounded-full px-6 py-3.5 text-sm font-medium bg-[#FDFCFB] text-[#121C17] hover:bg-white">Browse services</Link>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp"
                className="rounded-full px-6 py-3.5 text-sm font-medium border border-white/20 hover:bg-white/10">Chat on WhatsApp</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
